import React, { useState } from 'react';
import { Button } from '@app/components/Button/Button';
import CircleCheckedBlackIcon from '@app/assets/icons/circle-checked-black.svg?react';

interface ResolveFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResolve: (data: { sendFollowUp: boolean; notes?: string }) => void;
  isResolving?: boolean;
}

export const ResolveFeedbackModal: React.FC<ResolveFeedbackModalProps> = ({
  isOpen,
  onClose,
  onResolve,
  isResolving = false,
}) => {
  const [sendFollowUp, setSendFollowUp] = useState(true);
  const [notes, setNotes] = useState('');
  const [isLocalResolving, setIsLocalResolving] = useState(false);

  const handleResolve = async () => {
    setIsLocalResolving(true);
    try {
      const trimmedNotes = notes.trim();
      if (trimmedNotes) {
        await onResolve({
          sendFollowUp,
          notes: trimmedNotes,
        });
      } else {
        await onResolve({
          sendFollowUp,
        });
      }
    } finally {
      setIsLocalResolving(false);
    }
  };

  const handleClose = () => {
    setNotes('');
    setSendFollowUp(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-md">
              <CircleCheckedBlackIcon width={20} height={20} className="text-gray-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Resolve feedback</h2>
              <p className="text-sm text-gray-600 mt-1">
                Leave additional information about this resolution
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Follow-up Campaign Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSendFollowUp(!sendFollowUp)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    sendFollowUp ? 'bg-brand-baseBlue' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      sendFollowUp ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium text-gray-900">
                  Send a follow-up campaign to this customer
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 ml-14">
              The customer will receive the same email or SMS campaign that was sent earlier to leave a new review of your business after their problem is solved.
            </p>
          </div>

          {/* Notes Section */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-900">
              Notes <span className="text-gray-500">(Optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Description of the problem and solutions"
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6">
          <Button
            type="button"
            kind="secondary"
            onClick={handleClose}
            disabled={isResolving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleResolve}
            disabled={isLocalResolving || isResolving}
          >
            {isLocalResolving || isResolving ? 'Resolving...' : 'Resolve'}
          </Button>
        </div>
      </div>
    </div>
  );
};
